package com.example.readingnotes.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class ExifData(
    val make: String? = null,
    val model: String? = null,
    val dateTime: String? = null,
    val dateTimeOriginal: String? = null,
    val dateTimeDigitized: String? = null,
    val width: Int? = null,
    val height: Int? = null,
    val orientation: Int? = null,
    val xResolution: Float? = null,
    val yResolution: Float? = null,
    val resolutionUnit: Int? = null,
    val software: String? = null,
    val exposureTime: Float? = null,
    val fNumber: Float? = null,
    val iso: Int? = null,
    val focalLength: Float? = null,
    val flash: Boolean? = null,
    val whiteBalance: String? = null,
    val gpsLatitude: Double? = null,
    val gpsLongitude: Double? = null,
    val gpsAltitude: Double? = null,
    val artist: String? = null,
    val copyright: String? = null,
    val comment: String? = null,
) {
    /**
     * Get formatted exposure time (e.g., "1/250s")
     */
    fun getFormattedExposureTime(): String? =
        exposureTime?.let { exposure ->
            if (exposure < 1) {
                "1/${(1 / exposure).toInt()}s"
            } else {
                "${exposure}s"
            }
        }

    /**
     * Get formatted f-number (e.g., "f/2.8")
     */
    fun getFormattedFNumber(): String? = fNumber?.let { "f/$it" }

    /**
     * Get formatted focal length (e.g., "50mm")
     */
    fun getFormattedFocalLength(): String? = focalLength?.let { "${it}mm" }

    /**
     * Get orientation description
     */
    fun getOrientationDescription(): String? =
        when (orientation) {
            1 -> "通常"
            2 -> "水平反転"
            3 -> "180度回転"
            4 -> "垂直反転"
            5 -> "90度回転 + 水平反転"
            6 -> "時計回りに90度回転"
            7 -> "90度回転 + 垂直反転"
            8 -> "反時計回りに90度回転"
            else -> null
        }

    /**
     * Get GPS coordinates as formatted string
     */
    fun getFormattedGPS(): String? =
        if (gpsLatitude != null && gpsLongitude != null) {
            val lat = if (gpsLatitude!! >= 0) "$gpsLatitude°N" else "${-gpsLatitude!!}°S"
            val lng = if (gpsLongitude!! >= 0) "$gpsLongitude°E" else "${-gpsLongitude!!}°W"
            "$lat, $lng"
        } else {
            null
        }

    /**
     * Get white balance description
     */
    fun getWhiteBalanceDescription(): String? =
        when (whiteBalance) {
            "auto" -> "オート"
            "manual" -> "マニュアル"
            "daylight" -> "昼光"
            "cloudy" -> "曇天"
            "fluorescent" -> "蛍光灯"
            "tungsten" -> "白熱電球"
            "flash" -> "フラッシュ"
            else -> whiteBalance
        }

    /**
     * Check if image has GPS data
     */
    fun hasGPSData(): Boolean = gpsLatitude != null && gpsLongitude != null

    /**
     * Check if image has camera settings
     */
    fun hasCameraSettings(): Boolean = exposureTime != null || fNumber != null || iso != null || focalLength != null
}
